<template>
  <panel
    :breadcrumbs="breadcrumbs"
    :title="title"
  >
    <v-card>
      <v-card-title>
        <!-- STATUS (LOCKED UNTIL MONTH) -->
        <v-select
          v-model="filterStatus"
          label="Status"
          dense
          outlined
          class="mr-2"
          persistent-hint
          :hint="!filterMonth ? 'Select month first' : 'Optional'"
          :items="[
            { text: 'All', value: null },
            { text: 'Pending', value: 'pending' },
            { text: 'Approved', value: 'success' },
            { text: 'Rejected', value: 'reject' }
          ]"
        />
        <v-spacer />

        <!-- SEARCH -->
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Search"
          single-line
          solo-inverted
          clearable
          box
          hide-details
        />
      </v-card-title>

      <v-spacer />
      <v-data-table
        :headers="headers"
        :items="requests"
        :loading="loading"
        :search="search"
        item-key="id"
        class="px-1 pb-1"
      >
        <!-- TABLE ROWS -->
        <template
          slot="items"
          slot-scope="props"
        >
          <tr @click="selectRow(props.item)">
            <td>{{ props.item.dealerId }}</td>
            <td>{{ props.item.payoutMethodLabel }}</td>
            <td>
              <span
                v-if="props.item.status === 'pending_approval'"
                class="warning--text"
              >
                {{ props.item.status }}
              </span>
              <span
                v-if="props.item.status === 'reject_approval'"
                class="red--text"
              >
                {{ props.item.status }}
              </span>
              <span
                v-if="props.item.status === 'success_approval'"
                class="success--text"
              >
                {{ props.item.status }}
              </span>
            </td>
            <td>{{ props.item.createdAt }}</td>
          </tr>
        </template>

        <!-- NO DATA -->
        <template slot="no-data">
          <v-alert
            dense
            type="info"
          >
            No pending requests found.
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <!-- ================= DETAILS ================= -->
    <v-card
      v-if="selected"
      class="mt-4"
    >
      <v-card-title>
        Request details
        <v-spacer />
        <v-chip small>
          Dealer: {{ selected.dealerId }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <v-layout wrap>
          <v-flex
            xs12
            sm6
          >
            <strong>Payout Method:</strong>
            {{ selected.payoutMethodLabel }}
          </v-flex>

          <v-flex
            xs12
            sm6
          >
            <strong>Account Holder:</strong>
            {{ selected.tpName || selected.ownerName || '-' }}
          </v-flex>

          <v-flex
            xs12
            sm6
          >
            <strong>NRIC / BRN:</strong>
            {{ selected.tpId || selected.ownerId || '-' }}
          </v-flex>

          <v-flex
            xs12
            sm6
          >
            <strong>Bank:</strong>
            {{ selected.tpBankName || selected.bankName || '-' }}
          </v-flex>

          <v-flex
            xs12
            sm6
          >
            <strong>Account No:</strong>
            {{ selected.tpBankAccount || selected.bankAccount || '-' }}
          </v-flex>
        </v-layout>

        <v-divider class="my-3" />
        <v-card-title> Documents </v-card-title> <v-spacer /> <v-card-text>
          <v-layout wrap>
            <v-card-text class="pa-0">
              <table class="documents-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Status</th>
                    <th class="text-center">
                      View
                    </th>
                    <th class="text-center">
                      Re-upload
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="doc in documents"
                    :key="doc.key"
                  >
                    <td>{{ doc.label }}</td>
                    <td>
                      <span
                        v-if="doc.file"
                        class="success--text"
                      >Uploaded</span>
                      <span
                        v-else
                        class="error--text"
                      >Not uploaded</span>
                    </td>
                    <td class="text-center">
                      <v-btn
                        v-if="doc.file"
                        icon
                        @click="download(doc.file)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                      <span v-else>—</span>
                    </td>
                    <td class="text-center">
                      <v-btn
                        color="error"
                        outlined
                        small
                        @click="openReupload(doc)"
                      >
                        Re-upload
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-card-text>
          </v-layout>
        </v-card-text>
      </v-card-text>

      <v-card-actions v-if="canDecide">
        <v-spacer />

        <v-btn
          color="error"
          :loading="deciding === 'reject'"
          @click="decide('reject')"
        >
          Reject
        </v-btn>

        <v-btn
          color="primary"
          :loading="deciding === 'approve'"
          @click="decide('approve')"
        >
          Approve
        </v-btn>
      </v-card-actions>
      <!-- ================= 3RD PARTY VERIFICATION TABLE ================= -->

      <v-divider class="my-4" />

      <v-card-title>
        Uploaded Document
      </v-card-title>

      <v-data-table
        :headers="verificationHeaders"
        :items="verificationDocs"
        :loading="verificationLoading"
        item-key="id"
        class="elevation-1"
      >
        <template #items="props">
          <tr>
            <td>{{ props.item.dateUpload }}</td>
            <td>{{ props.item.dateVerified }}</td>
            <td>{{ props.item.dealer }}</td>
            <td>{{ props.item.title }}</td>
            <td>{{ props.item.status }}</td>

            <td class="text-center">
              <v-btn
                :href="props.item.image"
                target="_blank"
                icon
              >
                <v-icon small>
                  mdi-eye
                </v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog
      v-model="reuploadDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title>
          Re-upload: {{ reuploadDoc && reuploadDoc.label }}
          <v-spacer />
          <v-btn
            icon
            @click="reuploadDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <reupload3rdParty
            v-if="reuploadDoc"
            :key="reuploadDoc.key"
            :document-key="reuploadDoc.key"
            :title="reuploadDoc.label"
            :request-id="selected.id"
            admin-mode
            @uploaded="handleReuploadSuccess"
            @closeDialog="reuploadDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </panel>
</template>

<script>
import { createGetParams, createPostParams } from '@/rest'

export default {
  name: 'PayoutMethodApprovals',

  components: {
    Panel: () => import('@/components/Panel'),
    Reupload3rdParty: () => import('@/components/reupload3rdParty'),
  },

  data () {
    return {
      title: 'Payout Method Approvals',
      breadcrumbs: [
        {
          text: 'DMT Kits', disabled: false, to: '/support/dmt_kits',
        },
        {
          text: 'Payout Method Approvals', disabled: true,
        },
      ],
      headers: [
        { text: 'Dealer ID', value: 'dealerId' },
        { text: 'Payout Method', value: 'payoutMethodLabel' },
        { text: 'Status', value: 'status' },
        { text: 'Submitted At', value: 'createdAt' },
      ],
      verificationDocs: [],
      verificationLoading: false,

      verificationHeaders: [
        { text: 'Date Upload', value: 'dateUpload' },
        { text: 'Date Verified', value: 'dateVerified' },
        { text: 'Dealer', value: 'dealer' },
        { text: 'Category', value: 'title' },
        { text: 'Status', value: 'status' },
        { text: 'Document', value: 'image' },
      ],
      filterMonth: null, // YYYY-MM
      filterStatus: null,
      monthMenu: false,
      search: '',
      reuploadDialog: false,
      reuploadDoc: null,
      requests: [],
      selected: null,
      loading: false,
      deciding: null,
      rejectionNote: '',
    }
  },
  watch: {
    filterMonth () {
    // reset dependent filter
      this.filterStatus = null

      // auto reload
      this.loadQueue()
    },

    filterStatus () {
      this.loadQueue()
    },
  },

  computed: {
    documents () {
      if (!this.selected || !this.selected.uploads) return []

      const map = {
        consentLetterUpload: 'Consent Letter (Signed)',
        thirdPartyIc: '3rd Party IC',
        witnessIc: 'Witness IC',
        form49: 'Form 49',
        form51: 'Form 51',
        form58: 'Form 58',
        ssm: 'SSM',
      }

      return Object.keys(map).map(key => ({
        key,
        label: map[key],
        file: this.selected.uploads[key] || null,
      }))
    },
    canDecide () {
      if (!this.selected) return false

      return ![
        'success_approval',
        'success',
        'approved',
        'reject',
      ].includes(this.selected.status)
    },

  },
  mounted () {
  // ✅ visually set current month (backend already defaults too)
    this.filterMonth = this.$moment
      ? this.$moment().format('YYYY-MM')
      : new Date().toISOString().slice(0, 7)

    this.loadQueue()
  },

  methods: {
    handleReuploadClose () {
      this.reuploadDialog = false
      this.reuploadDoc = null
    },
    handleReuploadSuccess () {
    // Close dialog safely
      this.reuploadDialog = false
      this.reuploadDoc = null

      // 🔁 RE-FETCH FROM BACKEND
      const params = createGetParams({ id: this.selected.id })

      this.$rest.get('getPayoutMethodRequest.php', params)
        .then(res => {
          this.selected = {
            ...this.selected,
            ...res.data,
          }
        })
    },
    onAdminUploaded (payload) {
      if (!payload || !payload.fieldKey) return

      // Close dialog first
      this.reuploadDialog = false
      this.reuploadDoc = null

      // Re-fetch latest request from backend
      const params = createGetParams({ id: this.selected.id })

      this.$rest.get('getPayoutMethodRequest.php', params)
        .then(res => {
          this.selected = {
            ...this.selected,
            ...res.data,
          }
        })
    },
    openReupload (doc) {
      this.reuploadDoc = doc
      this.reuploadDialog = true
    },
    requestReupload (doc) {
      const reason = prompt(`Why is "${doc.label}" rejected?`)
      if (!reason) return

      const params = createPostParams({
        action: 'rejectPayoutDocument',
        payload: {
          requestId: this.selected.id,
          documentKey: doc.key,
          reason,
        },
      })

      this.$rest.post('postAction.php', params)
        .then(() => {
          alert('Re-upload requested')
          this.loadQueue()
          this.selected = null
        })
    },

    loadVerificationDocs () {
      if (!this.selected) return

      this.verificationLoading = true

      const params = createGetParams({
        dealer: this.selected.dealerId,
      })

      this.$rest.get('get3rdPartyVerification.php', params)
        .then(res => {
          this.verificationDocs = res.data.check || res.data.items || []
          this.verificationLoading = false
        })
        .catch(() => {
          this.verificationDocs = []
          this.verificationLoading = false
        })
    },

    download (file) {
      window.open(file.url, '_blank')
    },
    uploadLabel (key) {
      const map = {
        consentForm: 'Consent Form',
        thirdPartyIcFront: '3rd Party IC Front',
        thirdPartyIcBack: '3rd Party IC Back',
        witnessIcFront: 'Witness IC Front',
        witnessIcBack: 'Witness IC Back',
        ssm: 'SSM',
      }
      return map[key] || key
    },

    loadQueue () {
      this.loading = true

      const params = {
        month: this.filterMonth || null, // backend default if null
        status: this.filterStatus || null,
      }

      this.$rest.get('getPayoutMethodQueue.php', createGetParams(params))
        .then(res => {
          this.requests = res.data || []
          this.loading = false
        })
        .catch(() => {
          this.requests = []
          this.loading = false
        })
    },
    selectRow (item) {
      this.selected = null
      const params = createGetParams({ id: item.id })

      this.$rest.get('getPayoutMethodRequest.php', params)
        .then(res => {
          this.selected = {
            ...item,
            ...res.data,
          }

          // load verification table
          this.loadVerificationDocs()
        })
    },

    decide (decision) {
      if (!this.selected) return

      if (this.selected.status === 'success_approval') {
        return
      }

      if (decision === 'reject') {
        const note = prompt('Enter rejection reason')
        if (!note) return
        this.rejectionNote = note
      }

      const params = createPostParams({
        action: decision === 'approve'
          ? 'approvePayoutMethod'
          : 'rejectPayoutMethod',
        payload: {
          requestId: this.selected.id,
          rejectionReason: this.rejectionNote || undefined,
        },
      })

      this.$rest.post('postAction.php', params)
        .then(() => {
          this.selected = null
          this.loadQueue()
        })
    },
  },
}
</script>
<style>
.documents-table th,
.documents-table td {
  padding: 16px 20px;   /* ⬅️ more breathing room */
  font-size: 14px;
  vertical-align: middle;
}
.documents-table thead th {
  text-align: left !important;
}

.documents-table thead th.text-center {
  text-align: left !important;
}
.documents-table thead th {
  background: rgba(255, 255, 255, 0.03);
}

.documents-table th {
  font-weight: 600;
}
</style>
